// How long a click may wait for the browser to leave for checkout before the
// button is handed back: a navigation that never happened (blocked, offline)
// must not leave a dead button behind.
var FLIX_OD_NAVIGATION_TIMEOUT_MS = 8000;

class FlixOrderDetailsForm extends HTMLElement {
  connectedCallback() {
    this._continueBtn = this.querySelector('[data-flix-od-continue]');
    this._banner = this.querySelector('[data-flix-od-banner-success]');
    this._progressBar = this.querySelector('[data-flix-od-progress-bar]');
    this._progressText = this.querySelector('[data-flix-od-progress-text]');
    this._error = this.querySelector('[data-flix-od-error]');
    this._busy = false;

    this.addEventListener('input', () => this._recompute());
    this.addEventListener('change', () => this._recompute());
    if (this._continueBtn) this._continueBtn.addEventListener('click', () => this._continue());

    // Back/Forward from checkout. A page restored from the back-forward
    // cache comes back exactly as it was left — mid-redirect, button
    // disabled — and no script runs again. Hand the button back, and check
    // it against the cart again.
    this._onPageShow = (event) => {
      this._setBusy(false);
      if (event.persisted) this._synced = this._syncWithCart();
    };
    window.addEventListener('pageshow', this._onPageShow);

    // Firefox restores a button's disabled state on reload as well — and on
    // Back it may show its cached copy of this page, drawn before the
    // details were saved: empty fields, old line keys.
    this._setBusy(false);
    this._synced = this._syncWithCart();
    this._recompute();
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this._onPageShow);
    clearTimeout(this._navigationTimer);
  }

  _fields() {
    return Array.from(this.querySelectorAll('[data-flix-od-field]'));
  }

  _isFilled(fieldEl) {
    const input = fieldEl.querySelector('[data-flix-od-input]');
    return !!(input && input.value && input.value.trim());
  }

  // Several elements ship with an inline `display` for their visible state,
  // which outranks the browser's default [hidden]{display:none} rule. Toggle
  // style.display directly (keeping the hidden attribute in sync for a11y).
  _setVisible(el, show) {
    if (!el) return;
    if (show) {
      el.style.display = el.dataset.flixShowDisplay || '';
      el.removeAttribute('hidden');
    } else {
      if (!el.dataset.flixShowDisplay) el.dataset.flixShowDisplay = el.style.display || 'block';
      el.style.display = 'none';
      el.setAttribute('hidden', '');
    }
  }

  // "Required" only ever marks a field with an asterisk and feeds the
  // progress indicator — it never blocks checkout, since the order's actual
  // details can just as well be sorted out after purchase.
  _recompute() {
    const fields = this._fields();
    const required = fields.filter((f) => f.dataset.required === 'true');
    const filled = required.filter((f) => this._isFilled(f));
    const pct = required.length ? Math.round((filled.length / required.length) * 100) : 100;

    if (this._progressBar) this._progressBar.style.width = pct + '%';
    if (this._progressText) this._progressText.textContent = pct + '%';
    this._setVisible(this._banner, required.length > 0 && filled.length === required.length);

    fields.forEach((f) => {
      const isFilled = this._isFilled(f);
      f.dataset.state = isFilled ? 'filled' : '';
      const check = f.querySelector('[data-flix-od-check]');
      if (check) check.style.display = isFilled ? 'flex' : 'none';
    });
  }

  _setBusy(busy) {
    this._busy = busy;
    clearTimeout(this._navigationTimer);
    if (!this._continueBtn) return;
    this._continueBtn.disabled = busy;
    this._continueBtn.style.opacity = busy ? '.7' : '1';
    this._continueBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  // Checkout is the same cart every time (Shopify's /checkout for this
  // cart), so going there again after coming back never makes a second
  // order — only a second click while the first is still saving is refused.
  async _continue() {
    if (this._busy) return;
    this._setBusy(true);
    this._setVisible(this._error, false);
    try {
      // Never save from a page that has not been checked against the cart:
      // a stale copy would write its empty fields over the saved details.
      await this._synced;
      await this._persistProperties();
    } catch (err) {
      // Nothing is sent to checkout without the details it was given: the
      // customer stays here, with everything they typed, and can try again.
      this._setBusy(false);
      this._setVisible(this._error, true);
      return;
    }
    this._navigationTimer = setTimeout(() => this._setBusy(false), FLIX_OD_NAVIGATION_TIMEOUT_MS);
    window.location.href = window.Shopify.routes.root + 'checkout';
  }

  async _cart() {
    const res = await fetch(window.Shopify.routes.root + 'cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!res.ok) throw new Error('cart ' + res.status);
    return res.json();
  }

  // Each product's details go onto that product's own line, found by its
  // line key — not its position, which another tab may have changed. Saving
  // properties gives the line a new key, so the page keeps the new one for
  // the next save (another trip to checkout and back). A line the cart no
  // longer has under that key is found by its variant only when exactly one
  // line carries it; otherwise nothing is written anywhere.
  async _persistProperties() {
    const items = Array.from(this.querySelectorAll('[data-flix-od-item]'));
    let cart = await this._cart();
    for (const itemEl of items) {
      const fields = Array.from(itemEl.querySelectorAll('[data-flix-od-field]'));
      if (fields.length === 0) continue;

      const line = this._lineFor(cart, itemEl);
      if (!line) throw new Error('line not found');
      const index = cart.items.indexOf(line);

      // Only this form's own fields change; anything else on the line
      // (another app's hidden properties) is kept as it was.
      const properties = Object.assign({}, line.properties || {});
      fields.forEach((f) => {
        const input = f.querySelector('[data-flix-od-input]');
        if (input) properties[f.dataset.key] = input.value;
      });

      const res = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: line.quantity, properties: properties })
      });
      if (!res.ok) throw new Error('cart/change ' + res.status);
      cart = await res.json();
      // Same position, new key (the properties are part of it).
      if (cart.items[index]) itemEl.dataset.itemKey = cart.items[index].key;
    }
  }

  _lineFor(cart, itemEl) {
    const byKey = cart.items.find((line) => line.key === itemEl.dataset.itemKey);
    if (byKey) return byKey;
    const sameVariant = cart.items.filter((line) => String(line.variant_id) === itemEl.dataset.variantId);
    return sameVariant.length === 1 ? sameVariant[0] : null;
  }

  // The page as drawn against the cart as it is. Drawn from the same cart
  // (the usual case): nothing to do. Drawn from an older one — the
  // browser's cached copy, or a restored page — but the same products:
  // everything typed was saved before leaving, so the saved details and
  // the new line keys are put back into the page. Different products (a
  // finished payment emptied the cart, another tab changed it): the page is
  // drawn again from the cart, once.
  async _syncWithCart() {
    let cart;
    try {
      cart = await this._cart();
    } catch (err) {
      return;
    }
    const items = Array.from(this.querySelectorAll('[data-flix-od-item]'));
    const keys = items.map((el) => el.dataset.itemKey);
    if (keys.length === cart.items.length && keys.every((key, i) => key === cart.items[i].key)) return;

    const lines = items.map((el) => this._lineFor(cart, el));
    if (items.length === cart.items.length && lines.every(Boolean)) {
      items.forEach((itemEl, i) => {
        const properties = lines[i].properties || {};
        itemEl.dataset.itemKey = lines[i].key;
        itemEl.querySelectorAll('[data-flix-od-field]').forEach((f) => {
          const input = f.querySelector('[data-flix-od-input]');
          if (input && typeof properties[f.dataset.key] === 'string') input.value = properties[f.dataset.key];
        });
      });
      this._recompute();
      return;
    }
    this._reloadOnce();
  }

  // A reload that keeps finding the page out of step must not loop.
  _reloadOnce() {
    const KEY = 'flix-od-reloaded-at';
    try {
      const last = Number(window.sessionStorage.getItem(KEY) || 0);
      if (Date.now() - last < 10000) return;
      window.sessionStorage.setItem(KEY, String(Date.now()));
    } catch (err) {
      return;
    }
    window.location.reload();
  }
}
customElements.define('flix-order-details-form', FlixOrderDetailsForm);
